import { getBackendUrl } from '@/utils/apiUrl';
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { CreditCard, FileText, TrendingUp, DollarSign, Eye, Download, ChevronDown, Check, Clock, AlertCircle, ExternalLink, Search, X, Filter } from 'lucide-react';
import { toast } from 'sonner';

const Accounting = ({ user }) => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDropdown, setOpenDropdown] = useState(null);
  
  // Filter States
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('alle');
  const [dateFilter, setDateFilter] = useState('alle');
  
  const [stats, setStats] = useState({
    open: 0,
    paid: 0,
    overdue: 0,
    totalRevenue: 0
  });

  // Click-Outside-Handler: Dropdown schließen wenn woanders geklickt wird
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Prüfe ob Klick außerhalb eines Dropdowns war
      if (openDropdown && !event.target.closest('.status-dropdown-container')) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdown]);

  // Filtered invoices based on search and filters
  const filteredInvoices = useMemo(() => {
    return invoices.filter(invoice => {
      // Text-Suche (Rechnungsnr., Aktennr., Kunde)
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = !searchTerm || 
        (invoice.invoice_number || '').toLowerCase().includes(searchLower) ||
        (invoice.case_number || invoice.case?.case_number || '').toLowerCase().includes(searchLower) ||
        (invoice.customer_name || invoice.case?.customer_name || '').toLowerCase().includes(searchLower);
      
      // Status-Filter
      const matchesStatus = statusFilter === 'alle' || 
        (invoice.status || 'offen').toLowerCase() === statusFilter.toLowerCase();
      
      // Datum-Filter
      let matchesDate = true;
      if (dateFilter !== 'alle' && invoice.created_at) {
        const invoiceDate = new Date(invoice.created_at);
        const now = new Date();
        
        switch (dateFilter) {
          case 'heute':
            matchesDate = invoiceDate.toDateString() === now.toDateString();
            break;
          case 'woche':
            const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            matchesDate = invoiceDate >= weekAgo;
            break;
          case 'monat':
            matchesDate = invoiceDate.getMonth() === now.getMonth() && 
                         invoiceDate.getFullYear() === now.getFullYear();
            break;
          case 'jahr':
            matchesDate = invoiceDate.getFullYear() === now.getFullYear();
            break;
          default:
            matchesDate = true;
        }
      }
      
      return matchesSearch && matchesStatus && matchesDate;
    });
  }, [invoices, searchTerm, statusFilter, dateFilter]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch invoices
      const invoicesResponse = await fetch(getBackendUrl() + '/api/invoices', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      // Fetch cases for status
      const casesResponse = await fetch(getBackendUrl() + '/api/cases', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (invoicesResponse.ok && casesResponse.ok) {
        const invoicesData = await invoicesResponse.json();
        const casesData = await casesResponse.json();
        
        setInvoices(invoicesData);
        setCases(casesData);
        
        // Calculate stats
        const totalRevenue = invoicesData.reduce((sum, inv) => sum + (inv.total || 0), 0);
        const paidCount = invoicesData.length; // All invoices are considered paid when created
        
        // Count overdue (invoices older than 14 days)
        const fourteenDaysAgo = new Date();
        fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);
        const overdueCount = invoicesData.filter(inv => {
          const invoiceDate = new Date(inv.created_at);
          return invoiceDate < fourteenDaysAgo;
        }).length;
        
        // Open invoices = cases with 'abgerechnet' status
        const openCount = casesData.filter(c => c.status === 'abgerechnet').length;
        
        setStats({
          open: openCount,
          paid: paidCount,
          overdue: overdueCount,
          totalRevenue: totalRevenue
        });
      }
    } catch (error) {
      console.error('Fehler beim Laden der Daten:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', { 
      style: 'currency', 
      currency: 'EUR' 
    }).format(amount);
  };

  // Zahlungsstatus aktualisieren - optimiert für schnelle UI-Reaktion
  const updatePaymentStatus = async (invoiceId, newStatus) => {
    // Sofort Dropdown schließen und UI aktualisieren (optimistisch)
    setOpenDropdown(null);
    
    // Lokale Liste SOFORT aktualisieren (vor API-Call)
    setInvoices(prev => prev.map(inv => 
      inv._id === invoiceId ? { ...inv, status: newStatus } : inv
    ));
    
    // Stats sofort aktualisieren
    setStats(prev => {
      const oldStatus = invoices.find(inv => inv._id === invoiceId)?.status || 'erstellt';
      let newStats = { ...prev };
      
      // Alte Status-Zählung verringern
      if (oldStatus === 'erstellt') newStats.open = Math.max(0, newStats.open - 1);
      if (oldStatus === 'bezahlt') newStats.paid = Math.max(0, newStats.paid - 1);
      
      // Neue Status-Zählung erhöhen
      if (newStatus === 'erstellt') newStats.open += 1;
      if (newStatus === 'bezahlt') newStats.paid += 1;
      
      return newStats;
    });
    
    toast.success(`Status auf "${getStatusLabel(newStatus)}" geändert`);
    
    // API-Call im Hintergrund (ohne auf Antwort zu warten für UI)
    try {
      const response = await fetch(`${getBackendUrl()}/api/invoices/${invoiceId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) {
        // Bei Fehler: Status zurücksetzen
        toast.error('Fehler beim Speichern - Status zurückgesetzt');
        fetchData(); // Daten neu laden um konsistent zu bleiben
      }
    } catch (error) {
      console.error('Status update error:', error);
      toast.error('Fehler beim Speichern');
      fetchData();
    }
  };

  // Status-Label
  const getStatusLabel = (status) => {
    const labels = {
      'erstellt': 'Offen',
      'bezahlt': 'Bezahlt',
      'storniert': 'Storniert',
      'mahnung': 'Mahnung'
    };
    return labels[status] || status;
  };

  // Status-Farbe
  const getStatusColor = (status) => {
    const colors = {
      'erstellt': 'bg-yellow-100 text-yellow-800',
      'bezahlt': 'bg-green-100 text-green-800',
      'storniert': 'bg-red-100 text-red-800',
      'mahnung': 'bg-orange-100 text-orange-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  // PDF öffnen
  const openInvoicePdf = (pdfUrl) => {
    if (pdfUrl) {
      const fullUrl = pdfUrl.startsWith('http') ? pdfUrl : `${getBackendUrl()}${pdfUrl}`;
      window.open(fullUrl, '_blank');
    } else {
      toast.error('Keine PDF vorhanden');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
  };

  return (
    <Layout user={user}>
      <div className="p-4 md:p-8">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Buchhaltung</h1>
        <p className="text-sm md:text-base text-gray-600 mb-6 md:mb-8">Rechnungen und finanzielle Übersicht</p>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 mb-6 md:mb-8">
          <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm border" style={{borderColor: '#E1E4E8'}} data-testid="stat-open-invoices">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-4">
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center" style={{backgroundColor: '#DBEAFE'}}>
                <FileText className="w-5 h-5 md:w-6 md:h-6" style={{color: '#2563EB'}} />
              </div>
              <div>
                <div className="text-xs md:text-sm text-gray-600">Abgerechnete Fälle</div>
                <div className="text-xl md:text-2xl font-bold text-gray-900">{stats.open}</div>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm border" style={{borderColor: '#E1E4E8'}} data-testid="stat-paid-invoices">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-4">
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center" style={{backgroundColor: '#D1FAE5'}}>
                <TrendingUp className="w-5 h-5 md:w-6 md:h-6" style={{color: '#059669'}} />
              </div>
              <div>
                <div className="text-xs md:text-sm text-gray-600">Rechnungen Erstellt</div>
                <div className="text-xl md:text-2xl font-bold text-gray-900">{stats.paid}</div>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm border" style={{borderColor: '#E1E4E8'}} data-testid="stat-overdue-invoices">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-4">
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center" style={{backgroundColor: '#FEE2E2'}}>
                <DollarSign className="w-5 h-5 md:w-6 md:h-6" style={{color: '#DC2626'}} />
              </div>
              <div>
                <div className="text-xs md:text-sm text-gray-600">Älter als 14 Tage</div>
                <div className="text-xl md:text-2xl font-bold text-gray-900">{stats.overdue}</div>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm border" style={{borderColor: '#E1E4E8'}} data-testid="stat-total-revenue">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-4">
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center" style={{backgroundColor: '#E0F2FE'}}>
                <CreditCard className="w-5 h-5 md:w-6 md:h-6" style={{color: '#0891B2'}} />
              </div>
              <div>
                <div className="text-xs md:text-sm text-gray-600">Gesamtumsatz</div>
                <div className="text-xl md:text-2xl font-bold text-gray-900">{formatCurrency(stats.totalRevenue)}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Invoices Table */}
        <div className="bg-white rounded-lg shadow-sm border overflow-visible" style={{borderColor: '#E1E4E8'}}>
          <div className="p-4 md:p-6 border-b" style={{borderColor: '#E1E4E8'}}>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <h3 className="text-lg font-semibold text-gray-900">Rechnungen</h3>
              
              {/* Filter-Bereich */}
              <div className="flex flex-col md:flex-row gap-3">
                {/* Suchfeld */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Suche: Rechnung, Akte, Kunde..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full md:w-64"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                
                {/* Status-Filter */}
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                >
                  <option value="alle">Alle Status</option>
                  <option value="erstellt">Erstellt</option>
                  <option value="offen">Offen</option>
                  <option value="bezahlt">Bezahlt</option>
                  <option value="mahnung">Mahnung</option>
                  <option value="storniert">Storniert</option>
                </select>
                
                {/* Datum-Filter */}
                <select
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                >
                  <option value="alle">Alle Zeiträume</option>
                  <option value="heute">Heute</option>
                  <option value="woche">Letzte 7 Tage</option>
                  <option value="monat">Dieser Monat</option>
                  <option value="jahr">Dieses Jahr</option>
                </select>
              </div>
            </div>
            
            {/* Aktive Filter anzeigen */}
            {(searchTerm || statusFilter !== 'alle' || dateFilter !== 'alle') && (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className="text-xs text-gray-500">Filter:</span>
                {searchTerm && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                    Suche: "{searchTerm}"
                    <button onClick={() => setSearchTerm('')}><X className="w-3 h-3" /></button>
                  </span>
                )}
                {statusFilter !== 'alle' && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                    Status: {statusFilter}
                    <button onClick={() => setStatusFilter('alle')}><X className="w-3 h-3" /></button>
                  </span>
                )}
                {dateFilter !== 'alle' && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs">
                    Zeit: {dateFilter}
                    <button onClick={() => setDateFilter('alle')}><X className="w-3 h-3" /></button>
                  </span>
                )}
                <button
                  onClick={() => { setSearchTerm(''); setStatusFilter('alle'); setDateFilter('alle'); }}
                  className="text-xs text-gray-500 hover:text-gray-700 underline"
                >
                  Alle Filter zurücksetzen
                </button>
              </div>
            )}
            
            {/* Ergebnis-Anzahl */}
            <div className="mt-2 text-xs text-gray-500">
              {filteredInvoices.length} von {invoices.length} Rechnungen
            </div>
          </div>
          <div className="overflow-visible pb-40">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-4 border-gray-200 mx-auto" style={{borderTopColor: '#0A4D8C'}}></div>
              </div>
            ) : filteredInvoices.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                {invoices.length === 0 
                  ? 'Noch keine Rechnungen erstellt' 
                  : 'Keine Rechnungen gefunden mit den aktuellen Filtern'
                }
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50" style={{borderBottom: '1px solid #E1E4E8'}}>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rechnungsnr.
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Aktennr.
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Kunde
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fahrzeug
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Datum
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Betrag
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Aktionen
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y" style={{borderColor: '#E1E4E8'}}>
                  {filteredInvoices.map((invoice) => (
                    <tr 
                      key={invoice._id} 
                      className="hover:bg-gray-50 transition-colors"
                      data-testid={`invoice-${invoice._id}`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {invoice.invoice_number}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {invoice.case_id ? (
                          <button
                            onClick={() => navigate(`/new-case?id=${invoice.case_id}`)}
                            className="text-sm font-mono text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1 transition-colors"
                            title="Akte öffnen"
                          >
                            {invoice.case_number || invoice.case?.case_number || '-'}
                            <ExternalLink className="w-3 h-3" />
                          </button>
                        ) : (
                          <div className="text-sm font-mono text-gray-400">
                            {invoice.case_number || '-'}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {invoice.customer_name || invoice.case?.customer_name || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {invoice.case?.manufacturer} {invoice.case?.model}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDate(invoice.created_at)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="text-sm font-medium text-gray-900">
                          {formatCurrency(invoice.total)}
                        </div>
                      </td>
                      {/* Status mit Dropdown */}
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="relative inline-block status-dropdown-container">
                          <button
                            onClick={() => setOpenDropdown(openDropdown === invoice._id ? null : invoice._id)}
                            className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(invoice.status || 'erstellt')}`}
                          >
                            {getStatusLabel(invoice.status || 'erstellt')}
                            <ChevronDown className="w-3 h-3" />
                          </button>
                          {/* Dropdown-Menü */}
                          {openDropdown === invoice._id && (
                            <div className="absolute z-50 top-full mt-1 left-1/2 transform -translate-x-1/2 w-36 bg-white border border-gray-200 rounded-lg shadow-xl py-1">
                              <button
                                onClick={() => updatePaymentStatus(invoice._id, 'erstellt')}
                                className="flex items-center gap-2 w-full px-4 py-2 text-sm text-left hover:bg-gray-100"
                              >
                                <Clock className="w-4 h-4 text-yellow-500" />
                                Offen
                              </button>
                              <button
                                onClick={() => updatePaymentStatus(invoice._id, 'bezahlt')}
                                className="flex items-center gap-2 w-full px-4 py-2 text-sm text-left hover:bg-gray-100"
                              >
                                <Check className="w-4 h-4 text-green-500" />
                                Bezahlt
                              </button>
                              <button
                                onClick={() => updatePaymentStatus(invoice._id, 'mahnung')}
                                className="flex items-center gap-2 w-full px-4 py-2 text-sm text-left hover:bg-gray-100"
                              >
                                <AlertCircle className="w-4 h-4 text-orange-500" />
                                Mahnung
                              </button>
                              <button
                                onClick={() => updatePaymentStatus(invoice._id, 'storniert')}
                                className="flex items-center gap-2 w-full px-4 py-2 text-sm text-left hover:bg-gray-100 text-red-600"
                              >
                                <AlertCircle className="w-4 h-4" />
                                Storniert
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                      {/* Aktionen */}
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center gap-2">
                          {/* PDF öffnen */}
                          <button
                            onClick={() => openInvoicePdf(invoice.pdf_url)}
                            className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Rechnung öffnen"
                          >
                            <Eye className="w-5 h-5" />
                          </button>
                          {/* PDF herunterladen */}
                          <a
                            href={invoice.pdf_url ? `${getBackendUrl()}${invoice.pdf_url}` : '#'}
                            download
                            className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                            title="Rechnung herunterladen"
                          >
                            <Download className="w-5 h-5" />
                          </a>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Accounting;
